/**
 * stylesListScale.js — 미팅지도 **목록** 전용 글자 크기 (2026-09-11)
 *
 *   요청: "목록 글자를 더 크게 볼 수 있는 기능. 켜면 계속 유지되고 끄면 원래대로.
 *          (휴대폰에서, 잘 안 보이시는 분들이 많다)"
 *
 *   ⚠ 공유 스타일(stylesLayout/stylesItems)을 함수형으로 바꾸지 않는다.
 *     companyStyle·subTextStyle·ticketBadgeStyle·addressTextStyle 은 **바텀시트(지도에서
 *     마커를 눌렀을 때 뜨는 창)와 같이 쓴다.** 원본을 건드리면 거기까지 커진다.
 *     여기서는 원본을 **복사해 크기만 덮어쓴다** — 목록만 커진다.
 *
 *   ⚠ 크기만 키우면 오히려 더 안 보이는 자리가 셋 있어 함께 처리한다.
 *     ① 상호명은 원래 한 줄 말줄임(nowrap)이라, 키우면 **더 잘린다** → 크게일 때 줄바꿈 허용
 *     ② 내용 칸은 maxHeight:80 고정이라, 키우면 두 줄도 안 들어간다 → 비례 확대
 *     ③ 아이콘(연필·복사·버튼)은 px 고정이라 글자만 키우면 상대적으로 작아 보인다 → 같이 키움
 */
import {
  companyStyle, subTextStyle, ticketBadgeStyle, addressTextStyle,
  phoneSlashStyle, phoneParenStyle, phoneLinkStyle, phoneEmptyStyle,
  contentLabelStyle, contentValueStyle, emptyStyle, warnTextStyle,
  contractedChipStyle, cancelledChipStyle, keepChipStyle,
  feedbackChipStyle, cardResultIconBtnStyle, cardLockedIconStyle,
} from './styles';

/** 'normal' | 'large' → 배율. 값을 늘리려면 서버 ALLOWED_LIST_TEXT_SIZES 도 함께 늘릴 것. */
const SCALE = { normal: 1, large: 1.4 };

export const LIST_TEXT_SIZES = ['normal', 'large'];

const px = (base, scale) => Math.round(base * scale);

/**
 * 목록 카드가 쓰는 스타일 한 벌.
 * @param {'normal'|'large'} size
 */
export function buildListStyles(size) {
  const scale = SCALE[size] || 1;
  const big = scale > 1;

  return {
    scale,
    // 상호명 — 크게일 때는 말줄임을 풀어 두 줄까지 보이게 한다(잘려서 못 읽는 것을 막는다)
    company: big
      ? {
        ...companyStyle,
        fontSize: px(16, scale),
        whiteSpace: 'normal',
        overflow: 'visible',
        textOverflow: 'clip',
        lineHeight: 1.25,
      }
      : companyStyle,
    sub: big ? { ...subTextStyle, fontSize: px(12, scale) } : subTextStyle,
    ticket: big ? { ...ticketBadgeStyle, fontSize: px(11, scale), padding: '4px 11px' } : ticketBadgeStyle,
    address: big ? { ...addressTextStyle, fontSize: px(13, scale) } : addressTextStyle,
    phoneSlash: big ? { ...phoneSlashStyle, fontSize: px(13, scale) } : phoneSlashStyle,
    phoneParen: big ? { ...phoneParenStyle, fontSize: px(13, scale) } : phoneParenStyle,
    phoneLink: big ? { ...phoneLinkStyle, fontSize: px(13, scale) } : phoneLinkStyle,
    phoneEmpty: big ? { ...phoneEmptyStyle, fontSize: px(13, scale) } : phoneEmptyStyle,
    contentLabel: big ? { ...contentLabelStyle, fontSize: px(11, scale) } : contentLabelStyle,
    // 내용 — 글자와 함께 높이도 키우지 않으면 두 줄도 안 들어간다
    contentValue: big
      ? { ...contentValueStyle, fontSize: px(13, scale), maxHeight: px(80, scale) }
      : contentValueStyle,
    empty: big ? { ...emptyStyle, fontSize: px(13, scale) } : emptyStyle,
    warn: big ? { ...warnTextStyle, fontSize: px(12, scale) } : warnTextStyle,
    contractedChip: big ? { ...contractedChipStyle, fontSize: px(12, scale) } : contractedChipStyle,
    cancelledChip: big ? { ...cancelledChipStyle, fontSize: px(12, scale) } : cancelledChipStyle,
    // 안내 문구(목록 0건일 때) — MeetingListView 인라인 스타일
    hint: { marginTop: 6, fontSize: px(12, scale), opacity: 0.8 },
    keepChip: big ? { ...keepChipStyle, fontSize: px(11, scale) } : keepChipStyle,
    // 함수형 스타일 둘은 색상 인자를 그대로 넘기고 크기만 덮는다
    feedbackChip: (colorKey) => (big
      ? { ...feedbackChipStyle(colorKey), fontSize: px(12, scale), padding: '4px 11px' }
      : feedbackChipStyle(colorKey)),
    resultIconBtn: (hasResult, colorKey) => (big
      ? { ...cardResultIconBtnStyle(hasResult, colorKey), width: px(28, scale), height: px(28, scale) }
      : cardResultIconBtnStyle(hasResult, colorKey)),
    lockedIcon: big ? { ...cardLockedIconStyle, fontSize: px(14, scale) } : cardLockedIconStyle,
    // 아이콘·복사 버튼 — 글자만 키우면 상대적으로 작아 어색하다
    iconSize: px(13, scale),
    copyTextSize: px(12, scale),
  };
}
